// Re-export cn utility for shared components
export { cn } from "./tailwind";

// UUID pattern used in the LEGACY citation marker format (e.g. `1:550e8400-...`).
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/**
 * Strip inline citation markers from text for clean copy/insert.
 *
 * Mirrors the formats understood by `rehypeLinkifySources` so that anything
 * rendered as a citation badge is also stripped on copy:
 * - Current: `[ref:1, ref:2]` or bare `ref:N` — the ONLY format produced today
 *   by every backend pipeline (proposed answers, assistant chat, ontology
 *   follow-ups). N is a 1-based index; the UUID lives in `MessageReference.source_id`.
 * - Legacy: `[1:uuid, 2:uuid]` or bare `N:uuid` — NO LONGER PRODUCED. Kept only
 *   to render/strip old stored messages written before the format switch. The
 *   matching legacy branch in `rehypeLinkifySources` exists for the same reason.
 *   When that renderer branch is removed (e.g. once old messages age out), drop
 *   the two legacy `.replace()` calls below and the UUID constant together.
 *
 * Bracketed forms are removed before bare forms so a bare-marker regex can't
 * match inside a bracket and leave stray `[`/`]` behind.
 *
 * The backend resolves filename-form markers (`[ref:828D_LH2.pdf]`) into numeric
 * citations or strips them, so current messages never carry them. The extra
 * non-numeric branch below is a safety net for messages stored before that fix:
 * it removes any `[ref:<token>]` whose token is not purely numeric (so it is not
 * a renderable citation). Anchored on the literal `ref:` prefix, it cannot match
 * ordinary bracketed text like `[1]` or `[Hinweis]`.
 */
export const stripCitationMarkers = (text: string): string =>
  text
    .replace(/\s*\[ref:\s*\d+(?:\s*,\s*(?:ref:\s*)?\d+)*\]/g, "")
    .replace(
      new RegExp(`\\s*\\[(?:\\d+:${UUID}(?:\\s*,\\s*\\d+:${UUID})*)\\]`, "g"),
      "",
    )
    .replace(/\s*\[ref:\s*[A-Za-z0-9][A-Za-z0-9._-]*\]/g, "")
    .replace(/\s*ref:\s*\d+/g, "")
    .replace(new RegExp(`\\s*\\d+:${UUID}`, "g"), "");
